import type {
	Property,
	ShortVideoProperty,
	VideoViewInfo
} from '~/core/entities/property';

export type VideoType = 'native' | 'hls';

/** Entry-point property for Shorts analytics (`inicial_*` Mixpanel props). */
export interface ShortsEntryContext {
	entryCommercialId: string;
	entryAskingPrice: number | null;
	entryRegionName: string;
	/**
	 * True quando `entryCommercialId` veio de uma URL direta (sem carrossel de
	 * origem). O item desse commercialId usa poster `lqip` para nao disputar
	 * banda com o primeiro chunk do video.
	 */
	isDirectEntry?: boolean;
}

export type ReelItemProperty = Property | ShortVideoProperty;

export interface ReelItem {
	property: ReelItemProperty;
	reelKey?: string;
	videoUrl?: string | null;
	thumbnailUrl: string | null;
	images?: string[];
	videoType?: VideoType;
	videoView?: VideoViewInfo;
	isFromHistory?: boolean;
	/** Video width in pixels - used to determine portrait/landscape orientation */
	videoWidth?: number;
	/** Video height in pixels - used to determine portrait/landscape orientation */
	videoHeight?: number;
}

/**
 * Item visual montado pelo consumidor dentro do mesmo carrossel vertical.
 * O feed só conhece o slot a renderizar; a regra de negócio fica na página.
 */
export interface ReelCustomItem {
	type: 'custom';
	reelKey: string;
	slot: string;
}

export type ReelFeedItem = ReelItem | ReelCustomItem;

export function isReelCustomItem(
	item: ReelFeedItem | null | undefined
): item is ReelCustomItem {
	return Boolean(item && 'type' in item && item.type === 'custom');
}

export function getReelFeedItemKey(item: ReelFeedItem): string {
	if (item.reelKey) return item.reelKey;
	if (isReelCustomItem(item)) return `custom:${item.slot}`;
	return item.property.commercialId;
}

export interface PropertyReelsFeedProps {
	items: ReelFeedItem[];
	initialIndex?: number;
	initialMuted?: boolean;
	preloadFirst?: boolean;
	videoType?: VideoType;
	isLoading?: boolean;
	isReady?: boolean;
	hasMore?: boolean;
	hasMoreHistory?: boolean;
	/** Number of history items prepended before the entry-point item */
	historyCount?: number;
	showNavigation?: boolean;
	showExploreButton?: boolean;
	isPaused?: boolean;
	disableScroll?: boolean;
	emptyStateTitle?: string;
	emptyStateDescription?: string;
	emptyStateActionLabel?: string;
	hideHeaderControls?: boolean;
	disableKeyboard?: boolean;
	/** Enables Shorts Mixpanel payloads (`shorts_viewed`, share extras, etc.). */
	shortsEntry?: ShortsEntryContext;
	/** When true, renders the `#leading-item` slot as the first slide. */
	hasLeadingItem?: boolean;
	autoplayArmed?: boolean;
}

export interface PropertyReelsFeedItemProps {
	item: ReelItem;
	isActive: boolean;
	index: number;
	shouldLoad: boolean;
	videoType?: VideoType;
	isGlobalMuted?: boolean;
	isMobile?: boolean;
	/** When true, uses lighter buffer settings to reduce bandwidth while preloading */
	isPreloadOnly?: boolean;
}

export type PropertyReelsCloseReason = 'backdrop' | 'escape' | 'ui';

export interface PropertyReelsFeedEmits {
	(e: 'close', reason?: PropertyReelsCloseReason): void;
	(e: 'property-clicked', property: ReelItemProperty): void;
	(e: 'index-change', index: number, itemIndex: number): void;
	(e: 'current-item-change', item: ReelItem | null, itemIndex: number): void;
	(e: 'first-video-loaded'): void;
	(e: 'load-more'): void;
	(e: 'open-explore'): void;
	(e: 'empty-state-action'): void;
	(e: 'request-history'): void;
	(e: 'tap'): void;
}

export interface PropertyReelsFeedItemEmits {
	(e: 'property-clicked', property: ReelItemProperty): void;
	(e: 'video-play'): void;
	(e: 'video-pause'): void;
	(e: 'video-loaded'): void;
	(e: 'video-buffered'): void;
	/** Fires when the video first starts playing (earlier than buffered) */
	(e: 'video-playing'): void;
	(e: 'tap'): void;
	/** Fires when the active carousel image changes (image-only reels, desktop bg). */
	(e: 'carousel-image-change', url: string | null): void;
}
